import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ConfirmationModal from "../generic/ConfirmationModal";
import EditMaterialItemModal from "./EditMaterialItemModal";
import { InventoryRecord } from "../../types/InventoryItem";
import { deleteMaterialRecord, fetchInventoryRecords } from "../../services/MaterialAPI";
import MaterialTypeDropdown from "../shared/MaterialTypeDropdown";
import SizeDropdown from "../shared/SizeDropdown";
import List from "../generic/List";
import { MANAGE_MATERIAL_TABS } from "../../pages/MaterialsPage";

const MaterialItemList: React.FC = () => {
    const [ materials, setMaterials ] = useState<InventoryRecord[]>([]);
    const [ filteredMaterials, setFilteredMaterials ] = useState<InventoryRecord[]>([]);
    const [ selectedMaterialType, setSelectedMaterialType ] = useState<number | null>(null);
    const [ selectedSize, setSelectedSize ] = useState<number | null>(null);
    const [ selectedMaterialId, setSelectedMaterialId ] = useState<number | null>(null);
    const [ editingMaterial, setEditingMaterial ] = useState<InventoryRecord | null>(null);
    const isFetched = useRef(false);
    const navigate = useNavigate();

    const fetchMaterials = async () => {
        try {
            const response = await fetchInventoryRecords();
            setMaterials(response);
            setFilteredMaterials(response);
        } catch (error) {
            console.error("Error fetching materials records:", error);
        }
    };

    useEffect(() => {
        if (isFetched.current) return;
        isFetched.current = true;
        fetchMaterials();
    }, []);

    useEffect(() => {
        const filterResults = () => {
            let filtered = [ ...materials ];
            if (selectedMaterialType) {
                filtered = filtered.filter(
                    (material) => material.material_type_id === selectedMaterialType,
                );
            }
            if (selectedSize) {
                filtered = filtered.filter(
                    (material) => material.material_size_id === selectedSize,
                );
            }
            setFilteredMaterials(filtered);
        };
        filterResults();
    }, [ selectedMaterialType, selectedSize, materials ]);

    const openDeleteConfirmation = (material: InventoryRecord) => {
        setSelectedMaterialId(material.id);
    };

    const closeDeleteConfirmation = async () => {
        setSelectedMaterialId(null);
        await fetchMaterials();
    };

    const handleDelete = async () => {
        if (selectedMaterialId !== null) {
            try {
                await deleteMaterialRecord(selectedMaterialId);
                setMaterials((prev) =>
                    prev.filter((material) => material.id !== selectedMaterialId),
                );
            } catch (error: any) {
                console.error("Error deleting material:", error.message);
            } finally {
                closeDeleteConfirmation();
            }
        }
    };

    const openEditModal = (material: InventoryRecord) => {
        setEditingMaterial({ ...material });
    };

    const closeEditModal = async () => {
        setEditingMaterial(null);
        await fetchMaterials();
    };

    const handleEditSubmit = async (updatedMaterial: InventoryRecord) => {
        try {
            setMaterials((prev) =>
                prev.map((material) =>
                    material.id === updatedMaterial.id ? updatedMaterial : material,
                ),
            );
            closeEditModal();
        } catch (error: any) {
            console.error("Error editing material:", error.message);
        }
    };

    const handleCreateMaterial = () => {
        navigate(`/materials/${MANAGE_MATERIAL_TABS[1].endpoint}`);
    };

    return (
        <div className="w-full">
            <div className="flex items-center gap-4 mb-4">
                <MaterialTypeDropdown
                    value={selectedMaterialType}
                    onChange={(value) => setSelectedMaterialType(value)}
                />
                <SizeDropdown
                    selectedSize={selectedSize}
                    onSelectSize={(value) => setSelectedSize(value)}
                />
                <div className="ml-auto">
                    <button className={"primary"} onClick={handleCreateMaterial}>Create Material</button>
                </div>
            </div>

            <List
                items={filteredMaterials}
                onEdit={openEditModal}
                onDelete={openDeleteConfirmation}
                renderItem={(material) => (
                    <div>
                        <div className="text-gray-600 text-xs">
                            <p>Material ID: {material.id}</p>
                        </div>
                        <div className="text-md font-medium text-gray-800 mt-2 flex items-center space-x-2">
                            <h3>
                                {material.material_type} - {material.material_size} -{" "}
                                {material.color_name} ({material.color_code})
                            </h3>
                        </div>
                    </div>
                )}
                usePagination={true}
            />

            {selectedMaterialId !== null && (
                <ConfirmationModal
                    title="Confirm Deletion"
                    message="Are you sure you want to delete this material? This action cannot be undone."
                    onConfirm={handleDelete}
                    onCancel={closeDeleteConfirmation}
                />
            )}

            {editingMaterial && (
                <EditMaterialItemModal
                    currentItem={editingMaterial}
                    onClose={closeEditModal}
                    onSubmit={handleEditSubmit}
                />
            )}
        </div>
    );
};

export default MaterialItemList;